import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { Grid, CardContent, Typography, Box, Button, TextField, Paper } from "@mui/material";
import { getCustomerCustomAirports } from "src/redux/actions/Structure.Actions";
import { useDispatch } from "react-redux";
import { AirportForm } from "./AirportForm";
import { IActiveModal } from "src/pages/Admin/AdminWindow";
import { IAirport } from "@vmosolutions/oc-interfaces";
import { findAirports } from "src/utils/airportHelper";
import { DraggableListItem } from "src/components/DraggableListItem/DraggableListItem";
import AddIcon from "@mui/icons-material/Add";
import LoadingWrapper from "src/components/LoadingWrapper/LoadingWrapper";
import SearchIcon from "@mui/icons-material/Search";

interface IManageAirportsProps {
  id: string;
  setActiveModal: (activeModal: IActiveModal) => void;
  height: number;
}

export const ManageAirports = (props: IManageAirportsProps): JSX.Element => {
  const { id, setActiveModal, height } = props;
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [airportSearchString, setAirportSearchString] = useState<string>("");
  const [airports, setAirports] = useState<IAirport[]>([]);
  const [viewedAirports, setViewedAirport] = useState<IAirport[]>([]);
  const [selectedAirport, setSelectedAirport] = useState<IAirport | null>(null);

  const searchAirports = async (): Promise<IAirport[]> => {
    setIsLoading(true);
    const response: IAirport[] = await findAirports(airportSearchString);
    if (Array.isArray(response)) {
      setAirports(response);
    }
    setIsLoading(false);
    return response;
  };

  // When users select an airport from the search tool, add it to the array of previously viewed if not already there.
  useEffect(() => {
    if (!selectedAirport) return;
    setViewedAirport((prevViewed) => {
      const matchFound = prevViewed.find((ap) => ap.Ident === selectedAirport.Ident);
      return !matchFound ? [...prevViewed, selectedAirport] : prevViewed;
    });
  }, [selectedAirport]);

  const filterList = (): void => {
    setAirports(viewedAirports);
  };

  // // If users create, updated or delete customers, update list.
  const updateAirportsList = (updatedAP: IAirport, mode: "create" | "update" | "delete" | "showinlist"): void => {
    const newAirports = [...airports];
    const index = newAirports.findIndex((AP: IAirport) => AP._id === updatedAP._id);
    switch (true) {
      case updatedAP._id && mode === "showinlist":
        if (index === -1) {
          newAirports.push(updatedAP);
        }
        break;
      case updatedAP._id && (mode === "create" || mode === "update"): {
        const newAirport = { ...updatedAP, userModified: true };
        newAirports[index] = newAirport;
        setSelectedAirport(newAirport);
        break;
      }
      case updatedAP._id && mode === "delete" && index > -1:
        newAirports.splice(index, 1);
        setSelectedAirport(null);
        break;
      default:
    }
    setAirports(newAirports);
    dispatch(getCustomerCustomAirports());
  };
  return (
    <CardContent>
      <Grid container spacing={3}>
        <Grid item xs={2} md={3}>
          <Paper elevation={4} sx={{ height: `calc(${height}px - 50px)`, overflowY: "scroll", overflowX: "hidden" }}>
            <Grid container item xs={12}>
              <Grid item xs>
                <TextField
                  placeholder="Search by name, IATA or ICAO code"
                  variant="outlined"
                  size="small"
                  id="airportSearchString"
                  fullWidth
                  onChange={(e) => {
                    setAirportSearchString(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      searchAirports();
                    }
                  }}
                />
              </Grid>
              <Grid item xs={"auto"}>
                <Button
                  color="info"
                  variant="contained"
                  size="small"
                  onClick={() => searchAirports()}
                  sx={{
                    cursor: "pointer",
                    height: "100%",
                    borderBottomLeftRadius: 0,
                    borderTopLeftRadius: 0,
                    borderBottomRightRadius: 0,
                  }}
                >
                  <SearchIcon fontSize="small" />
                </Button>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Button
                fullWidth
                color="success"
                variant="contained"
                size="small"
                onClick={() => setSelectedAirport(null)}
                sx={{ cursor: "pointer", borderTopRightRadius: 0 }}
              >
                <AddIcon fontSize="small" /> Add Airport
              </Button>
            </Grid>
            <Grid container item xs={12}>
              <Grid item sx={{ overflowY: "scroll", width: "100%" }}>
                {isLoading && (
                  <Box sx={{ py: 3, px: 2 }}>
                    <LoadingWrapper message="Loading airports..." />
                  </Box>
                )}
                {airports.map((airport, key) => {
                  return (
                    <DraggableListItem
                      key={key}
                      active={selectedAirport && airport._id === selectedAirport?._id ? true : false}
                      onClick={() => setSelectedAirport(airport)}
                      sx={{ cursor: "pointer", display: "flex", alignItems: "start" }}
                    >
                      <Box sx={{ pl: 1 }}>({airport?.Ident})</Box>
                      <Box sx={{ pl: 1 }}>{airport?.Name}</Box>
                    </DraggableListItem>
                  );
                })}
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid container item xs={10} md={9}>
          <Grid item xs={12} sx={{ overflowY: "scroll", height: `calc(${height}px - 50px)`, width: "100%" }}>
            <Typography variant="h6" sx={{ pb: 1 }}>
              Manage Airports
            </Typography>
            <AirportForm
              id={id}
              selectedAirport={selectedAirport}
              updateAirportsList={updateAirportsList}
              setActiveModal={setActiveModal}
              loadedAirports={airports}
            />
          </Grid>
        </Grid>
      </Grid>
    </CardContent>
  );
};
